import z from "zod";
import {
  BookkeepingAccountIdScheme,
  ModifierGroupIdScheme,
  ModifierIdScheme,
  PrepStationIdScheme,
  ProductGroupIdScheme,
  ProductIdScheme,
  UnitIdScheme,
  LayoutIdScheme,
  ProductPageIdScheme,
} from "./ids";
import { RestCreateProduct, RestUpdateProduct } from "./product";
import { RestCreateProductGroup, RestUpdateProductGroup } from "./productGroup";
import { RestCreateModifier, RestUpdateModifier } from "./modifier";
import {
  RestCreateModifierGroup,
  RestUpdateModifierGroup,
} from "./modifierGroup";
import { RestCreateUnit, RestUpdateUnit } from "./unit";
import { RestCreatePrepStation, RestUpdatePrepStation } from "./prepStation";
import {
  RestCreateBookkeepingAccount,
  RestUpdateBookkeepingAccount,
} from "./bookkeepingAccount";
import { FloorPlanTableType } from "./enums/FloorPlanTableType";
import { FloorPlanDecorationType } from "./enums/FloorPlanDecorationType";
import { RestUpdateFloorPlan } from "./floorPlan";
import { RestCreateLayout, RestUpdateLayout } from "./layout";
import { LayoutItemScheme } from "./layoutItem";
import { RestCreateProductPage, RestUpdateProductPage } from "./productPage";
import {
  idOrOperationRef,
  idOrOperationRefArray,
  nullableIdOrOperationRef,
} from "./changeRequestRefs";

export const ChangeRequestProductCreatePayloadScheme = RestCreateProduct.extend(
  {
    groupId: nullableIdOrOperationRef(ProductGroupIdScheme),
    unitId: nullableIdOrOperationRef(UnitIdScheme).optional(),
    prepStationId: nullableIdOrOperationRef(PrepStationIdScheme).optional(),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    modifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
    modifierGroups: idOrOperationRefArray(ModifierGroupIdScheme).optional(),
    alwaysAppliedModifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
  },
);

export const ChangeRequestProductUpdateChangesScheme = RestUpdateProduct.extend(
  {
    groupId: nullableIdOrOperationRef(ProductGroupIdScheme).optional(),
    unitId: nullableIdOrOperationRef(UnitIdScheme).optional(),
    prepStationId: nullableIdOrOperationRef(PrepStationIdScheme).optional(),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    modifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
    modifierGroups: idOrOperationRefArray(ModifierGroupIdScheme).optional(),
    alwaysAppliedModifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
  },
);

export const ChangeRequestProductUpdatePayloadScheme = z.object({
  productId: idOrOperationRef(ProductIdScheme),
  changes: ChangeRequestProductUpdateChangesScheme,
});

export const ChangeRequestGroupCreatePayloadScheme =
  RestCreateProductGroup.extend({
    unitId: nullableIdOrOperationRef(UnitIdScheme).nullable().optional(),
    prepStationId: nullableIdOrOperationRef(PrepStationIdScheme).optional(),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    takeawayBookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    modifiers: idOrOperationRefArray(ModifierIdScheme).nullable().optional(),
    modifierGroups: idOrOperationRefArray(ModifierGroupIdScheme)
      .nullable()
      .optional(),
    alwaysAppliedModifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
  });

export const ChangeRequestGroupUpdateChangesScheme =
  RestUpdateProductGroup.extend({
    unitId: nullableIdOrOperationRef(UnitIdScheme).nullable().optional(),
    prepStationId: nullableIdOrOperationRef(PrepStationIdScheme).optional(),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    takeawayBookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    modifiers: idOrOperationRefArray(ModifierIdScheme).nullable().optional(),
    modifierGroups: idOrOperationRefArray(ModifierGroupIdScheme)
      .nullable()
      .optional(),
    alwaysAppliedModifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
  });

export const ChangeRequestModifierCreatePayloadScheme =
  RestCreateModifier.extend({
    productGroupId: nullableIdOrOperationRef(ProductGroupIdScheme),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    takeawayBookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
  });

export const ChangeRequestModifierUpdateChangesScheme =
  RestUpdateModifier.extend({
    productGroupId: nullableIdOrOperationRef(ProductGroupIdScheme).optional(),
    bookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
    takeawayBookkeepingAccountId: nullableIdOrOperationRef(
      BookkeepingAccountIdScheme,
    ).optional(),
  });

export const ChangeRequestModifierGroupCreatePayloadScheme =
  RestCreateModifierGroup.extend({
    modifiers: idOrOperationRefArray(ModifierIdScheme),
  });

export const ChangeRequestModifierGroupUpdateChangesScheme =
  RestUpdateModifierGroup.extend({
    modifiers: idOrOperationRefArray(ModifierIdScheme).optional(),
  });

export const ChangeRequestUnitCreatePayloadScheme = RestCreateUnit;

export const ChangeRequestUnitUpdateChangesScheme = RestUpdateUnit;

export const ChangeRequestPrepStationCreatePayloadScheme =
  RestCreatePrepStation;

export const ChangeRequestPrepStationUpdateChangesScheme =
  RestUpdatePrepStation;

export const ChangeRequestBookkeepingAccountCreatePayloadScheme =
  RestCreateBookkeepingAccount;

export const ChangeRequestBookkeepingAccountUpdateChangesScheme =
  RestUpdateBookkeepingAccount;

export const ChangeRequestFloorPlanTableDraftScheme = z.object({
  name: z.string(),
  type: z.enum(FloorPlanTableType),
  x: z.number().optional(),
  y: z.number().optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  rotation: z.number().optional(),
});

export const ChangeRequestFloorPlanDecorationDraftScheme = z.object({
  type: z.enum(FloorPlanDecorationType),
  name: z.string().optional(),
  x: z.number().optional(),
  y: z.number().optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  rotation: z.number().optional(),
});

export const ChangeRequestFloorPlanCreatePayloadScheme = z.object({
  name: z.string(),
  mapEnabled: z.boolean().optional(),
  mapWidth: z.number().positive().optional(),
  mapHeight: z.number().positive().optional(),
  tables: z.array(ChangeRequestFloorPlanTableDraftScheme),
  decorations: z.array(ChangeRequestFloorPlanDecorationDraftScheme).optional(),
});

export const ChangeRequestFloorPlanUpdateChangesScheme =
  RestUpdateFloorPlan.partial();

export const ChangeRequestLayoutCreatePayloadScheme = RestCreateLayout.omit({
  origin: true,
}).extend({
  defaultProductPageId: nullableIdOrOperationRef(ProductPageIdScheme),
});

export const ChangeRequestLayoutUpdateChangesScheme = RestUpdateLayout.omit({
  origin: true,
}).extend({
  defaultProductPageId:
    nullableIdOrOperationRef(ProductPageIdScheme).optional(),
});

export const ChangeRequestProductPageCreatePayloadScheme =
  RestCreateProductPage.omit({ origin: true }).extend({
    layoutId: idOrOperationRef(LayoutIdScheme).optional(),
  });

export const ChangeRequestProductPageUpdateChangesScheme =
  RestUpdateProductPage.omit({ origin: true }).extend({
    layoutId: idOrOperationRef(LayoutIdScheme).optional(),
  });

export const ChangeRequestProductPageAddItemsPayloadScheme = z.object({
  productPageId: idOrOperationRef(ProductPageIdScheme),
  items: z.array(LayoutItemScheme).min(1),
});
